import { Inject, Injectable, OnDestroy } from '@angular/core';
import { catchError, distinctUntilChanged, EMPTY, Observable, switchMap, timer } from 'rxjs';

import {
  DPU_ERROR_DESCRIPTIONS,
  DpuErrorCode,
  DpuErrorDescription,
  ICommunicationContext,
  UserFilesPathType,
} from 'shared-business/communication';
import { DpuFileSystemClient } from 'shared-business/file-system';
import { IFileManagerService } from 'shared-modules-angular/file-manager';
import { INotificationService, NOTIFICATION_SERVICE } from 'shared-modules-angular/notification';
import { nonNullish } from 'shared-utils/data';
import { FileItem, FileMetadataFilter, FileSort, FileType, StorageItem } from 'shared-utils/file-system';
import { deepEqual } from 'shared-utils/utils';

import { DPU_SERVICE } from '../dpu-file.token';

export type CommandFilesError = {
  id: number,
  method: 'copyFiles' | 'moveFiles',
  code: DpuErrorCode,
  message: string,
  data?: {
    files: {
      code: DpuErrorCode,
      name: string,
      description: DpuErrorDescription,
    }[],
  },
};

@Injectable({
  providedIn: 'root',
})
export class DpuFileSystemService implements IFileManagerService, OnDestroy {
  private readonly _dpuFileSystemClient: DpuFileSystemClient;

  constructor(
    @Inject(DPU_SERVICE) private readonly _dpuService: ICommunicationContext,
    @Inject(NOTIFICATION_SERVICE) private readonly _notificationService: INotificationService,
  ) {
    this._dpuFileSystemClient = new DpuFileSystemClient(
      this._dpuService,
    );
  }

  public getUserFilePathByFileType(fileType: UserFilesPathType): Observable<string | undefined> {
    return this._dpuFileSystemClient.getUserFilesPathByFileType(fileType);
  }

  public getFileNames(folderPath: string): Observable<string[] | undefined> {
    return this._dpuFileSystemClient.getFileNames(folderPath).pipe(
      catchError((error) => {
        console.error('Error getting file names from DPU', error);

        return EMPTY;
      }),
    );
  }

  public getFiles$(path: string, fileTypeFilter: FileType, sorting: FileSort | undefined, filters: FileMetadataFilter[] | undefined): Observable<FileItem[]> {
    return this._dpuFileSystemClient.getFiles(
      path,
      fileTypeFilter,
      sorting,
      filters,
    ).pipe(
      catchError((error) => {
        this._notificationService.sendNotification({
          type: 'assertive',
          variant: 'failure',
          key: error.message,
          additionalInfo: { error },
          actions: {},
        });

        return EMPTY;
      }),
    );
  }

  public moveFile(source: string, destination: string): Observable<undefined> {
    return this._dpuFileSystemClient.moveFiles([{ source, destination }]).pipe(
      catchError((error: CommandFilesError) => {
        throw new Error(
          this.getCommandFilesErrorMessageKey(error, 'ERRORS.FILE_SYSTEM.TRANSFER'),
          { cause: error },
        );
      }),
    );
  }

  public copyFile(source: string, destination: string): Observable<undefined> {
    return this._dpuFileSystemClient.copyFiles([{ source, destination }]).pipe(
      catchError((error: CommandFilesError) => {
        throw new Error(
          this.getCommandFilesErrorMessageKey(error, 'ERRORS.FILE_SYSTEM.COPY'),
          { cause: error },
        );
      }),
    );
  }

  public deleteFile(path: string): Observable<undefined> {
    return this._dpuFileSystemClient.deleteFiles([path]);
  }

  public getSources$(): Observable<StorageItem[]> {
    return this.longPollingSource$();
  }

  public ngOnDestroy(): void {
    this._dpuFileSystemClient.dispose();
    this._dpuService.dispose();
  }

  protected longPollingSource$(): Observable<StorageItem[]> {
    return timer(0, 1000).pipe(
      switchMap(() =>
        this._dpuFileSystemClient.getStorageLocations(),
      ),
      // prevents sending sources update if they haven't really changed
      distinctUntilChanged((oldStorageItems, newStorageItems) =>
        deepEqual(oldStorageItems, newStorageItems),
      ),
    );
  }

  private getCommandFilesErrorMessageKey(error: CommandFilesError, messageKey: string): string {
    const fileErrorCode: number | undefined = error?.data?.files?.[0]?.code;

    if (nonNullish(fileErrorCode) && nonNullish(DPU_ERROR_DESCRIPTIONS[fileErrorCode])) {
      messageKey = DPU_ERROR_DESCRIPTIONS[fileErrorCode];
    } else if (nonNullish(DPU_ERROR_DESCRIPTIONS[error?.code])) {
      messageKey = DPU_ERROR_DESCRIPTIONS[error.code];
    }

    return messageKey;
  }
}
